/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css, html, TemplateResult } from 'lit';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import C4DContentBlock from '../content-block/content-block';
import styles from './content-block-headlines.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Content Block Headlines
 *
 * @element c4d-content-block-headlines
 */
@customElement(`${c4dPrefix}-content-block-headlines`)
class C4DContentBlockHeadlines extends StableSelectorMixin(C4DContentBlock) {
  /**
   * The CSS class list for the container (grid) node.
   */
  // eslint-disable-next-line class-methods-use-this
  protected _getContainerClasses() {
    return `${prefix}--content-layout ${prefix}--content-layout--with-headlines ${prefix}--layout--border`;
  }

  protected _renderInnerBody(): TemplateResult | string | void {
    const { _hasContent: hasContent, _handleSlotChange: handleSlotChange } =
      this;
    return html`
      <div
        ?hidden="${!hasContent}"
        class="${prefix}--content-block-headlines__item-container">
        <slot @slotchange="${handleSlotChange}"></slot>
      </div>
    `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--content-block-headlines`;
  }

  // `styles` here is a `CSSResult` generated by custom WebPack loader
  static get styles() {
    return css`
      ${super.styles}${styles}
    `;
  }
}

export default C4DContentBlockHeadlines;
